import type { RefObject } from 'react'
import { toPng } from 'html-to-image'

/**
 * Returns a click handler that exports the referenced DOM element as a
 * downloaded PNG image.
 *
 * Use this for plain HTML/CSS visualisations (e.g. stacked bar summaries)
 * that have no underlying chart-library instance to export from. For
 * ECharts-based charts, use {@link useExportChartImage} instead, which
 * calls the chart instance's own `getDataURL()` rather than rasterising
 * the DOM.
 *
 * @param elementRef - Ref to the DOM node to rasterise. If the ref isn't
 * attached yet (`null`), the returned handler is a no-op.
 * @param filename - Download filename, without extension. `.png` is
 * appended automatically.
 * @returns An async click handler with no parameters and no return value.
 */
export function useExportDomImage(elementRef: RefObject<HTMLElement | null>, filename: string) {
  return async () => {
    if (!elementRef.current) return

    const dataUrl = await toPng(elementRef.current, {
      backgroundColor: '#ffffff',
      pixelRatio: 2,
    })

    const a = document.createElement('a')
    a.href = dataUrl
    a.download = `${filename}.png`
    a.click()
  }
}
